import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { CHECKBOX_SX, Pill } from '../../Pill.js';
import type { ResultSelection } from '../../workflows.types.js';
import type { IdeationBrief } from '../brief/briefResult.js';
import { IdeationBriefCard } from '../brief/IdeationBriefCard.js';
import { CARD_SX, GRID_SX, selectionSummary } from '../primitives.js';
import { STRATEGY_COLORS } from '../strategies.js';
import { StoryboardDrawer } from './StoryboardDrawer.js';
import type { StoryboardResult } from './storyboardResult.js';

function Pick({
  name,
  selection,
}: {
  name: string;
  selection: ResultSelection | undefined;
}): ReactNode {
  return selection === undefined ? null : (
    <Checkbox
      size="small"
      checked={selection.selectedIds.includes(name)}
      onChange={() => selection.onToggle(name)}
      sx={{ ...CHECKBOX_SX, flexShrink: 0 }}
      slotProps={{ input: { 'aria-label': `Select ${name}` } }}
    />
  );
}

export function SourceBriefStrip({ brief }: { brief: IdeationBrief | undefined }): ReactNode {
  const [open, setOpen] = useState(false);
  if (brief === undefined) return null;
  const elementCount = brief.categories.reduce(
    (count, category) => count + category.elements.length,
    0,
  );
  return (
    <>
      <Stack
        direction="row"
        sx={{
          p: '12px 16px',
          gap: '16px',
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '10px',
          bgcolor: 'background.paper',
        }}
      >
        <Stack sx={{ flex: 1, minWidth: 0, gap: '2px' }}>
          <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary' }}>
            Source brief
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{brief.name}</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            {elementCount} selected element{elementCount === 1 ? '' : 's'}
          </Typography>
        </Stack>
        <Button size="small" onClick={() => setOpen(true)}>
          View brief
        </Button>
      </Stack>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="storyboard-brief-title"
        fullWidth
        maxWidth="md"
      >
        <DialogTitle id="storyboard-brief-title">Brief details</DialogTitle>
        <DialogContent>
          <IdeationBriefCard brief={brief} />
        </DialogContent>
      </Dialog>
    </>
  );
}

// One version's summary card; `children` is the kind-specific body under the rationale.
export function VersionSummaryCard({
  version,
  index,
  selection,
  onOpen,
  children,
}: {
  version: { name: string; strategy: string; strategy_label: string; rationale: string };
  index: number;
  selection: ResultSelection | undefined;
  onOpen?: () => void;
  children?: ReactNode;
}): ReactNode {
  const color = STRATEGY_COLORS[version.strategy] ?? '#155DFC';
  const selected = selection?.selectedIds.includes(version.name) ?? false;
  const heading = (
    <>
      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: '8px' }}>Version {index + 1}</Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{version.rationale}</Typography>
    </>
  );
  return (
    <Stack
      data-testid={`storyboard-summary-${version.name}`}
      data-selected={selected}
      sx={(theme) => ({
        ...CARD_SX,
        p: '12px',
        borderRadius: '18px',
        gap: '12px',
        ...(selected
          ? {
              borderColor: 'primary.main',
              boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            }
          : {}),
      })}
    >
      <Stack direction="row" sx={{ gap: '8px', alignItems: 'center' }}>
        <Pick name={version.name} selection={selection} />
        {version.strategy_label === '' ? null : (
          <Pill
            sx={{
              bgcolor: alpha(color, 0.1),
              color,
              fontSize: 9,
              borderRadius: '4px',
              whiteSpace: 'normal',
            }}
          >
            {version.strategy_label.toUpperCase()}
          </Pill>
        )}
      </Stack>
      {onOpen === undefined ? (
        <Box>{heading}</Box>
      ) : (
        <Box
          component="button"
          type="button"
          onClick={onOpen}
          aria-label={`View Version ${index + 1}: ${version.name}`}
          sx={{
            p: 0,
            border: 0,
            bgcolor: 'transparent',
            color: 'inherit',
            textAlign: 'left',
            cursor: 'pointer',
            minHeight: 40,
          }}
        >
          {heading}
        </Box>
      )}
      {children}
    </Stack>
  );
}

export function StoryboardView({
  result,
  selection,
}: {
  result: StoryboardResult;
  selection?: ResultSelection;
}): ReactNode {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <Stack sx={{ gap: '24px', minWidth: 0 }}>
      <SourceBriefStrip brief={result.storyboard.source_brief} />
      <Box sx={{ ...GRID_SX, gap: '16px', alignItems: 'start' }}>
        {result.versions.map((version, index) => (
          <VersionSummaryCard
            key={`${version.name}-${version.strategy}-${version.creative_premise}`}
            version={version}
            index={index}
            selection={selection}
            onOpen={() => setOpen(index)}
          >
            <Stack direction="row" sx={{ gap: '6px', flexWrap: 'wrap' }}>
              {version.display_chips.map((chip) => (
                <Pill
                  key={chip}
                  sx={{
                    bgcolor: 'primary.bg',
                    color: 'text.secondary',
                    borderRadius: '4px',
                    fontSize: 10,
                  }}
                >
                  {chip}
                </Pill>
              ))}
            </Stack>
          </VersionSummaryCard>
        ))}
      </Box>
      <Stack
        direction="row"
        sx={{
          pt: '16px',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
          {selection === undefined
            ? ''
            : selectionSummary(selection.selectedIds.length, result.versions.length)}
        </Typography>
        <Button variant="gradient" onClick={() => setOpen(0)}>
          View storyboards
        </Button>
      </Stack>
      {open === null ? null : (
        <StoryboardDrawer
          result={result}
          open
          initialVersion={open}
          onClose={() => setOpen(null)}
          {...(selection === undefined ? {} : { selection })}
        />
      )}
    </Stack>
  );
}
